import { useUser } from '@folklore/auth';
import { useRoutes } from '@folklore/routes';
import { useEffect, useMemo } from 'react';
import { useRoute } from 'wouter';

import { useConsentGiven } from '../contexts/ConsentContext';
import { usePromotions } from '../contexts/PromotionsContext';

export function useAccess(
    document:
        | (Partial<FullDocument> & Partial<DocumentSnippet> & Partial<CollectionSnippet>)
        | null,
): Access | null {
    const { type, collection = null, access, serie: collectionSerie, metadata } = document || {};
    const { collections } = metadata || {};
    const metadataCollection = collections && collections.length > 0 ? collections[0] : null;
    const { access: collectionAccess, serie = collectionSerie } = metadataCollection || collection || {};
    const { access: serieAccess } = serie || {};
    return access !== null ||
        collectionAccess !== null ||
        serieAccess !== null ||
        type === 'micromag'
        ? {
              blocked: type === 'micromag',
              ...access,
              ...serieAccess,
              ...collectionAccess,
          }
        : null;
}

export function useIsBlocked(
    document:
        | (Partial<FullDocument> & Partial<DocumentSnippet> & Partial<CollectionSnippet>)
        | null,
): boolean {
    const { blocked = false } = useAccess(document) || {};
    return blocked;
}

export function useHasAccess(access: Access | null) {
    const { promotion: accessPromotion = null, blocked = false } = access || {};
    const { subscriptions = [] } = (accessPromotion as SubscriptionPromotion) || {};
    const { subscriptions: userSubscriptions = [] } = useUser() || {};
    const userSubscriptionsIds = (userSubscriptions || [])
        .filter(({ subscribed = false }) => subscribed)
        .map(({ id }) => id);
    const subscribed =
        (subscriptions.length > 0 &&
            subscriptions.findIndex(
                (subscription) => userSubscriptionsIds.indexOf(subscription) !== -1,
            ) !== -1) ||
        (subscriptions.length === 0 && userSubscriptionsIds.length > 0);
    return !blocked || subscribed;
}

export function useAccessPromotion(
    access: Access | null,
    opts: { type?: string; disabled?: boolean; triggers?: Trigger[]; delay?: number } = {},
) {
    const { disabled = false, triggers = null, type = null, delay = 5000 } = opts;
    const { blocked = false, promotion: accessPromotion } = access || {};
    const { addPromotion, removePromotion, allPromotions } = usePromotions();
    const routes = useRoutes();
    const consentGivent = useConsentGiven();
    const [matchMicromag = false, params] = useRoute(routes.micromag);
    // const api = useApi();
    const { path: screenParam = null } = params || {};
    const routeScreenIndex = matchMicromag ? parseInt(screenParam || '1', 10) - 1 : null;
    const typePromotions = allPromotions.filter(
        ({ id = null, handle = null }) =>
            (handle || id || '').match(new RegExp(`^access_${type}`, 'gi')) !== null,
    );
    const accessPromotions =
        typePromotions.length > 0
            ? typePromotions
            : allPromotions.filter(
                  ({ id = null, handle = null }) =>
                      (handle || id || '').match(/^access_all/) !== null,
              );
    const promotion = useMemo(() => {
        if (!blocked || disabled || (consentGivent === null && !matchMicromag)) {
            return null;
        }
        const currentPromotion =
            accessPromotions[Math.floor(Math.random() * accessPromotions.length)] || null;
        const {
            id: accessPromotionId = null,
            handle: accessPromotionHandle = null,
            screenIndex: subscriptionScreenIndex = null,
            blockLandingScreen = false,
            title: accessPromotionTitle = null,
            description: accessPromotionDescription = null,
            trigger: accessTrigger = null,
        } = accessPromotion || {};

        const finalSubscriptionScreenIndex =
            subscriptionScreenIndex !== null ? subscriptionScreenIndex : 2;
        const shouldAllowCurrentScreen =
            routeScreenIndex >= finalSubscriptionScreenIndex && !blockLandingScreen;

        const {
            id: promotionId,
            handle: promotionHandle,
            title: promotionTitle,
            description: promotionDescription,
        } = currentPromotion || {};

        const defaultTriggers = matchMicromag
            ? [
                  shouldAllowCurrentScreen
                      ? {
                            event: 'pageview',
                            pageMatch: {
                                type: 'micromag',
                            },
                            maxScreenIndex: routeScreenIndex - 1,
                        }
                      : null,
                  {
                      event: 'pageview',
                      pageMatch: {
                          type: 'micromag',
                      },
                      minScreenIndex: shouldAllowCurrentScreen
                          ? routeScreenIndex + 1
                          : finalSubscriptionScreenIndex,
                  },
              ]
            : type === 'video'
              ? [
                    {
                        event: 'video_play',
                    },
                ]
              : [
                    {
                        event: 'document_progress',
                        documentProgress: 0.1,
                    },
                    // {
                    //     event: 'register',
                    // },
                    // {
                    //     event: 'pageview',
                    // },
                ];

        return {
            ...accessPromotion,
            ...currentPromotion,
            id: accessPromotionId || promotionId,
            handle: accessPromotionHandle || promotionHandle,
            title: accessPromotionTitle || promotionTitle,
            description: accessPromotionDescription || promotionDescription,
            trigger: {
                event: 'one',
                delay,
                ...accessTrigger,
                triggers: [
                    ...(accessTrigger !== null ? accessTrigger.triggers || [] : []),
                    ...defaultTriggers,
                    ...(triggers || []),
                ].filter((it) => it !== null),
            },
        };
    }, [
        accessPromotion,
        // accessPromotions
        //     .map(({ id }) => id)
        //     .sort()
        //     .join(','),
        disabled,
        triggers,
        matchMicromag,
        consentGivent,
    ]);

    useEffect(() => {
        if (promotion === null) {
            return () => {};
        }
        addPromotion(promotion);
        return () => {
            removePromotion(promotion.id);
        };
    }, [promotion]);
}

export default useAccessPromotion;
